import { makeAutoObservable } from "mobx";
import userStore from "./userStore";
import profileStore from "./profileStore";
import type { ExtendedUser } from "@/types/user";
import type {
  QueuedAward,
  QueuedClimb,
  QueuedCourse,
} from "@/components/celebration/queueTypes";

type ActiveError = {
  errorText: string;
  errorObject: unknown;
  fatal: boolean;
};

type ActiveNextStep = unknown;

type CurrentUser = ExtendedUser | null;

class SessionStore {
  _currentUser: CurrentUser = null;
  careerClimbCompletionData: QueuedClimb[] = [];
  activeErrors: ActiveError[] = [];
  newlyCompletedAwards: QueuedAward[] = [];
  newlyCompletedCourses: QueuedCourse[] = [];
  clusterContentIndex: Record<string, number> = {};
  activeNextStep: ActiveNextStep = null;
  // How many immersive cluster activity views are currently open (video/audio
  // full-window panes, activities, ladders). >0 means the climber is inside an
  // activity rather than browsing the cluster menu. A counter (not a boolean)
  // so overlapping/rapid enter-exit transitions can't wedge the flag.
  clusterActivityImmersionCount = 0;

  get currentUser(): CurrentUser {
    console.error(
      "🚨🚨🚨 ERROR: Don't get currentUser from sessionStore! This won't work right. 🚨🚨🚨",
    );
    return this._currentUser;
  }

  set currentUser(value: CurrentUser) {
    this._currentUser = value;
  }

  constructor() {
    makeAutoObservable(this);
  }

  reportError(errorText: string, errorObject: unknown, fatal = true) {
    this.activeErrors.push({ errorText, errorObject, fatal });
  }

  setActiveNextStep(nextStep: ActiveNextStep) {
    this.activeNextStep = nextStep;
  }

  clearActiveNextStep() {
    this.activeNextStep = null;
  }

  // Multi-slot queue of detected-but-not-yet-acknowledged career climb
  // completions. Idempotent on id so re-detection doesn't duplicate.
  // Replaces the array on each push (rather than mutating in place) so
  // observers that key off the array's identity — e.g. useMemo deps in the
  // celebration registrar — actually recompute when an item lands.
  addCareerClimbCompletion(climb: QueuedClimb | null | undefined) {
    if (!climb) return;
    const incomingId = String(climb.id);
    if (
      this.careerClimbCompletionData.some(
        (existing) => String(existing.id) === incomingId,
      )
    ) {
      return;
    }
    this.careerClimbCompletionData = [...this.careerClimbCompletionData, climb];
  }

  removeCareerClimbCompletionById(id: string | number) {
    const target = String(id);
    this.careerClimbCompletionData = this.careerClimbCompletionData.filter(
      (c) => String(c.id) !== target,
    );
  }

  updateReceivedSurvey() {
    if (this._currentUser) this._currentUser.receivedSurvey = true;
  }

  setValuesScoresForLadder(ladderId: string | number, valuesScores: unknown) {
    const user = this._currentUser as
      | (ExtendedUser & { values?: { ladderId: string | number }[] })
      | null;
    if (!user) return;
    const oldScores =
      user.values?.filter((score) => score.ladderId !== ladderId) || [];
    user.values = [...oldScores, valuesScores as { ladderId: string | number }];
    this.setCurrentUser(user);
  }

  setCurrentUser(user: CurrentUser) {
    this._currentUser = user;
    userStore.setUser(user);
  }

  removeCurrentUser() {
    this._currentUser = null;
    userStore.removeUser();
    profileStore.clearProfile();
  }

  addNewlyCompletedAward(award: QueuedAward) {
    this.newlyCompletedAwards = [...this.newlyCompletedAwards, award];
  }

  addNewlyCompletedCourse(course: QueuedCourse) {
    this.newlyCompletedCourses = [...this.newlyCompletedCourses, course];
  }

  getClusterContentIndex(clusterId: string) {
    return this.clusterContentIndex[clusterId] || 0;
  }

  setClusterContentIndex(clusterId: string, contentIndex: number) {
    this.clusterContentIndex[clusterId] = contentIndex;
  }

  enterClusterActivity() {
    this.clusterActivityImmersionCount++;
  }

  exitClusterActivity() {
    this.clusterActivityImmersionCount = Math.max(
      0,
      this.clusterActivityImmersionCount - 1,
    );
  }

  resetClusterActivity() {
    this.clusterActivityImmersionCount = 0;
  }

  get isClusterActivityImmersed() {
    return this.clusterActivityImmersionCount > 0;
  }
}

const sessionStore = new SessionStore();
export default sessionStore;
